import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 5) { setStatus("timeout"); return; }
    try {
      const { data } = await API.get(`/payments/status/${sid}`);
      if (data.payment_status === "paid") {
        setStatus("success");
        await checkAuth();
      } else if (data.status === "expired") {
        setStatus("error");
      } else {
        setTimeout(() => pollStatus(sid, attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    }
  };

  useEffect(() => {
    if (sessionId) pollStatus(sessionId, 0);
    else setStatus("error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--ia-surface)]" data-testid="payment-success-page">
      <Card className="w-full max-w-md border-[var(--ia-border)]">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <div className="animate-fade-in">
              <Loader2 className="h-12 w-12 animate-spin text-[var(--ia-navy)] mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Vérification du paiement...</h2>
              <p className="text-sm text-[var(--ia-text-muted)]">Veuillez patienter pendant que nous confirmons votre paiement.</p>
            </div>
          )}
          {status === "success" && (
            <div className="animate-fade-in">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Paiement réussi !</h2>
              <p className="text-sm text-[var(--ia-text-muted)] mb-6">Votre compte a été mis à niveau vers Premium. Profitez de toutes les fonctionnalités !</p>
              <Button onClick={() => navigate("/dashboard")} className="bg-[var(--ia-navy)]" data-testid="payment-go-dashboard-btn">
                Accéder au dashboard
              </Button>
            </div>
          )}
          {(status === "error" || status === "timeout") && (
            <div className="animate-fade-in">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {status === "timeout" ? "Vérification en cours" : "Erreur de paiement"}
              </h2>
              <p className="text-sm text-[var(--ia-text-muted)] mb-6">
                {status === "timeout" ? "La vérification prend plus de temps que prévu. Votre paiement sera traité sous peu." : "Une erreur est survenue. Contactez le support si vous avez été débité."}
              </p>
              <Button onClick={() => navigate("/dashboard")} className="bg-[var(--ia-navy)]" data-testid="payment-error-dashboard-btn">
                Retour au dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
